import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword
  // deleteUser는 백엔드 Admin SDK에서 처리
} from 'firebase/auth';
import { auth } from './config';
import { getApiUrl } from '../utils/apiConfig';
import { createInAppBrowserAuthError, isEmbeddedWebView } from '../utils/inAppBrowser';
// 마이그레이션 관련 import 제거

const emailVerificationSettings = () => ({
  url: `${window.location.origin}/auth?tab=login&verified=true`,
  handleCodeInApp: false
});

const passwordResetSettings = () => ({
  url: `${window.location.origin}/auth?tab=login&reset=true`,
  handleCodeInApp: false
});

const requestCustomVerificationEmail = async (user) => {
  const idToken = await user.getIdToken(true);
  const response = await fetch(getApiUrl('/api/user/auth/send-verification-email'), {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json'
    }
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.detail || data.error || 'Custom verification email failed');
    error.status = response.status;
    throw error;
  }
  return data;
};

const requestCustomPasswordResetEmail = async (email) => {
  const response = await fetch(getApiUrl('/api/user/auth/send-password-reset-email'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.detail || data.error || 'Custom password reset email failed');
    error.status = response.status;
    throw error;
  }
  return data;
};

const sendVerificationEmailWithFallback = async (user) => {
  try {
    const result = await requestCustomVerificationEmail(user);
    if (result.already_verified) {
      return { alreadyVerified: true, delivery: 'custom' };
    }
    return { success: true, delivery: 'custom' };
  } catch (customError) {
    if (customError.status === 429) {
      throw customError;
    }
    console.warn('커스텀 인증 메일 발송 실패, Firebase 기본 메일로 대체:', customError);
    await sendEmailVerification(user, emailVerificationSettings());
    return { success: true, delivery: 'firebase_fallback' };
  }
};

const sendPasswordResetEmailWithFallback = async (email) => {
  try {
    await requestCustomPasswordResetEmail(email);
    return { success: true, delivery: 'custom' };
  } catch (customError) {
    if (customError.status === 429) {
      throw customError;
    }
    console.warn('커스텀 비밀번호 재설정 메일 발송 실패, Firebase 기본 메일로 대체:', customError);
    await sendPasswordResetEmail(auth, email, passwordResetSettings());
    return { success: true, delivery: 'firebase_fallback' };
  }
};

// 이메일/비밀번호로 회원가입 (이메일 인증 포함)
export const registerWithEmail = async (email, password, displayName, marketingAgreed = false) => {
  try {
    // 사용자 생성
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // 사용자 프로필 업데이트
    await updateProfile(user, { displayName });
    
    // 백엔드에 사용자 정보 및 마케팅 동의 정보 전송
    try {
      const getApiBaseUrl = () => {
        const currentUrl = window.location.hostname;
        if (currentUrl.includes('localhost')) {
          return 'http://localhost:8000';
        } else if (currentUrl.includes('whatquiztest')) {
          return 'https://quiz-api-test-397277209875.us-central1.run.app';
        } else {
          return 'https://quiz-api-397277209875.us-central1.run.app';
        }
      };
      
      const API_BASE_URL = getApiBaseUrl();
      const idToken = await user.getIdToken();
      
      // localStorage에서 UTM 파라미터 조회 (last-touch + first-touch)
      let signupUtm = {};
      try {
        const stored = localStorage.getItem('whatquiz_utm');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (!parsed.expiresAt || Date.now() <= parsed.expiresAt) {
            signupUtm = {
              utm_source: parsed.utm_source || null,
              utm_medium: parsed.utm_medium || null,
              utm_campaign: parsed.utm_campaign || null,
              utm_content: parsed.utm_content || null,
              utm_term: parsed.utm_term || null,
            };
          }
        }
      } catch (_e) { /* ignore */ }
      
      // first-touch UTM (최초 유입 보존 — ROAS 귀속용)
      try {
        const firstStored = localStorage.getItem('whatquiz_first_utm');
        if (firstStored) {
          const firstParsed = JSON.parse(firstStored);
          if (!firstParsed.expiresAt || Date.now() <= firstParsed.expiresAt) {
            signupUtm.first_utm = {
              utm_source: firstParsed.utm_source || null,
              utm_medium: firstParsed.utm_medium || null,
              utm_campaign: firstParsed.utm_campaign || null,
              utm_content: firstParsed.utm_content || null,
              utm_term: firstParsed.utm_term || null,
              landing_page: firstParsed.landing_page || null,
              referrer: firstParsed.referrer || null,
              first_visit: firstParsed.first_visit || null,
            };
          }
        }
      } catch (_e) { /* ignore */ }
      
      // Anonymous ID 포함 (UTM 스티칭용)
      try {
        const { getAnonymousUserId } = await import('../utils/anonymousUser');
        signupUtm.anonymous_id = getAnonymousUserId();
      } catch (_e) { /* ignore */ }

      const response = await fetch(`${API_BASE_URL}/api/user/ensure-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          marketing_consent: marketingAgreed,
          consent_date: new Date().toISOString(),
          ...signupUtm,
        })
      });
      
      if (!response.ok) {
        console.warn('백엔드 사용자 정보 생성 실패, 하지만 회원가입은 진행');
      } else {
        //console.log('마케팅 동의 정보가 백엔드에 저장되었습니다:', marketingAgreed);
      }
    } catch (backendError) {
      console.warn('백엔드 사용자 정보 전송 실패:', backendError);
      // 백엔드 오류는 회원가입을 막지 않음
    }
    
    // 이메일 인증 메일 발송: 왓퀴즈 브랜드 메일 우선, 실패 시 Firebase 기본 메일 fallback
    // 계정은 이미 만들어졌으므로 메일 실패를 가입 실패로 돌려주면 재시도가 "이미 사용 중"으로 막힌다.
    try {
      const emailResult = await sendVerificationEmailWithFallback(user);
      return { user, emailSent: true, emailDelivery: emailResult.delivery };
    } catch (mailError) {
      console.warn('가입 후 인증 메일 발송 실패:', mailError?.message);
      return { user, emailSent: false, emailDelivery: null };
    }
  } catch (error) {
    console.error('회원가입 오류:', error.message);
    return { error };
  }
};

// 이메일/비밀번호로 로그인
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user };
  } catch (error) {
    console.error('로그인 오류:', error.message);
    return { error };
  }
};

// 구글 계정으로 로그인
// - 웹(Chrome/Safari) / Android TWA: Firebase JS SDK signInWithPopup
// - Capacitor 네이티브 앱(iOS/Android Capacitor):
//   1) 네이티브 Google Sign-In SDK로 idToken 획득 (@capacitor-firebase/authentication)
//   2) idToken으로 Firebase JS SDK signInWithCredential 호출 (Firestore 등 web layer 동기화)
//
// ※ TWA(Chrome 셸)는 Chrome user-agent를 그대로 사용해 Google이 정상 인정하므로
//   isCapacitorNative()로 엄격히 분기하여 TWA 회귀 방지.
export const loginWithGoogle = async () => {
  try {
    const { isCapacitorNative } = await import('../utils/isInApp');

    if (isCapacitorNative()) {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      // 1. Native: Google Sign-In SDK 호출하여 credential 획득
      const result = await FirebaseAuthentication.signInWithGoogle({
        skipNativeAuth: true,
      });
      const idToken = result.credential?.idToken;
      if (!idToken) {
        throw new Error('Google 로그인 실패: idToken을 받지 못했습니다.');
      }
      // 2. Web layer: idToken으로 Firebase JS SDK 로그인 (Firestore/Storage 동기화)
      const credential = GoogleAuthProvider.credential(idToken);
      const userCredential = await signInWithCredential(auth, credential);
      return { user: userCredential.user };
    }

    if (isEmbeddedWebView()) {
      return { error: createInAppBrowserAuthError() };
    }

    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    return { user: userCredential.user };
  } catch (error) {
    console.error('구글 로그인 오류:', error.message);
    return { error };
  }
};

// 로그아웃
// - 웹/TWA: Firebase JS SDK signOut만 호출
// - Capacitor 네이티브 앱(iOS/Android Capacitor): 네이티브 세션 + Firebase 세션 모두 정리
export const logout = async () => {
  try {
    try {
      const { isCapacitorNative } = await import('../utils/isInApp');
      if (isCapacitorNative()) {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        await FirebaseAuthentication.signOut();
      }
    } catch (e) {
      console.warn('네이티브 로그아웃 실패(무시 가능):', e);
    }

    await signOut(auth);

    return { success: true };
  } catch (error) {
    console.error('로그아웃 오류:', error.message);
    return { error };
  }
};

// 비밀번호 재설정 이메일 전송
export const resetPassword = async (email) => {
  try {
    return await sendPasswordResetEmailWithFallback(email);
  } catch (error) {
    console.error('비밀번호 재설정 이메일 전송 오류:', error.message);
    return { error };
  }
};

// 이메일/비밀번호 로그인 수단을 실제로 보유한 계정인지 확인
export const hasPasswordProvider = (user) => (
  Array.isArray(user?.providerData)
  && user.providerData.some((provider) => provider?.providerId === 'password')
);

// 로그인된 이메일 계정의 비밀번호 변경
// 보안상 현재 비밀번호로 Firebase 재인증을 완료한 뒤에만 변경한다.
export const changePassword = async (currentPassword, newPassword) => {
  let stage = 'session';

  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      const error = new Error('로그인된 사용자가 없습니다.');
      error.code = 'auth/no-current-user';
      throw error;
    }

    if (!currentUser.email || !hasPasswordProvider(currentUser)) {
      const error = new Error('이메일과 비밀번호로 로그인한 계정이 아닙니다.');
      error.code = 'auth/password-provider-required';
      throw error;
    }

    if (
      typeof currentPassword !== 'string'
      || currentPassword.length === 0
      || typeof newPassword !== 'string'
      || newPassword.length === 0
    ) {
      stage = 'validation';
      const error = new Error('현재 비밀번호와 새 비밀번호가 필요합니다.');
      error.code = 'auth/invalid-password-input';
      throw error;
    }

    stage = 'reauthenticate';
    const credential = EmailAuthProvider.credential(
      currentUser.email,
      currentPassword
    );
    await reauthenticateWithCredential(currentUser, credential);

    stage = 'update';
    await updatePassword(currentUser, newPassword);

    return { success: true };
  } catch (error) {
    // 비밀번호 원문은 어떤 로그에도 남기지 않는다.
    console.error('비밀번호 변경 오류:', error?.code || 'auth/unknown');
    return { error, stage };
  }
};

// 이메일 인증 재발송
export const resendEmailVerification = async () => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('로그인된 사용자가 없습니다.');
    }
    
    if (currentUser.emailVerified) {
      return { alreadyVerified: true };
    }
    
    const result = await sendVerificationEmailWithFallback(currentUser);
    if (result.alreadyVerified) {
      return { alreadyVerified: true };
    }
    
    return { success: true, emailDelivery: result.delivery };
  } catch (error) {
    console.error('이메일 인증 재발송 오류:', error.message);
    return { error };
  }
};

// 회원 탈퇴
export const deleteAccount = async () => {
  try {
    // 현재 로그인된 사용자
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('로그인된 사용자가 없습니다.');
    }
    
    // API 기본 URL 설정
    const getApiBaseUrl = () => {
      const currentUrl = window.location.hostname;
      if (currentUrl.includes('localhost')) {
        return 'http://localhost:8000';
      } else if (currentUrl.includes('whatquiztest')) {
        return 'https://quiz-api-test-397277209875.us-central1.run.app';
      } else {
        return 'https://quiz-api-397277209875.us-central1.run.app';
      }
    };
    
    // 1. 먼저 백엔드 API를 통해 모든 데이터 삭제
    const API_BASE_URL = getApiBaseUrl();
    
    // 토큰 갱신 시도 (강제로 새 토큰 요청)
    let idToken;
    try {
      idToken = await currentUser.getIdToken(true); // force refresh
    } catch (tokenError) {
      console.error('토큰 갱신 실패:', tokenError);
      throw new Error('로그인이 만료됐어요. 다시 로그인한 뒤 탈퇴를 진행해주세요');
    }
    
    const response = await fetch(`${API_BASE_URL}/api/user/account`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      if (response.status === 401 || response.status === 403) {
        throw new Error('로그인이 만료됐어요. 다시 로그인한 뒤 탈퇴를 진행해주세요');
      }
      
      throw new Error(errorData.detail || '계정 삭제에 실패했습니다.');
    }
    
    // 백엔드 삭제 성공 확인
    const result = await response.json();
    
    // Admin SDK 삭제 후에도 웹/네이티브 SDK의 로컬 세션은 남을 수 있다.
    // 명시적으로 로그아웃해 stale token이 UI에서 재사용되지 않게 한다.
    await logout();
    
    return {
      success: true,
      confirmationEmailSent: Boolean(
        result?.deleted_data_summary?.confirmation_email_sent
      )
    };
  } catch (error) {
    console.error('회원 탈퇴 오류:', error.message);
    return { error };
  }
};